/**
 * Read-only Exhibition Logistics RFQ — buyer submitted view (design mockup).
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  FaBriefcase,
  FaCalendarAlt,
  FaCheck,
  FaCog,
  FaFileAlt,
  FaListUl,
  FaMapMarkerAlt,
  FaReply,
  FaTruck,
  FaUser,
} from 'react-icons/fa';
import { LuBuilding2, LuCalendar, LuDownload, LuFileText, LuMail, LuPhone, LuUser } from 'react-icons/lu';
import CommonService from '../../services/common.service';
import { LOGISTICS_TRANSPORT_MODES } from '../../constants/logisticsProfileFormOptions';
import { EL_PACKAGING_OPTIONS, formatExhibitionDimensionsDisplay } from './exhibitionLogisticsRfqFormState';
import { downloadExhibitionLogisticsRfqPdf } from '../../utils/exhibitionLogisticsRfqPdf';
import { getCurrentAssigneeDisplay } from '../../components/logistics/LogisticsRfqAssignedUserBadge';
import '../../assets/styles/exhibition_logistics_rfq.scss';
import '../../assets/styles/exhibition_logistics_rfq_submitted.scss';

function mapCountriesResponse(response) {
  const apiCountries = Array.isArray(response?.data)
    ? response.data
    : response?.data?.countries || response?.data?.master_countries || [];
  return apiCountries
    .map((country) => {
      const id = country.country_id ?? country.id ?? country.value;
      const label =
        country.country_name ?? country.country_label ?? country.label ?? country.name ?? country.country;
      return { id: id != null ? String(id) : '', label: label != null ? String(label) : '' };
    })
    .filter((c) => c.id && c.label);
}

function mapStatesResponse(response) {
  const apiStates = Array.isArray(response?.data)
    ? response.data
    : response?.data?.states || response?.data?.master_states || [];
  return apiStates
    .map((state) => {
      const id = state.state_id ?? state.id ?? state.value;
      const label = state.state_name ?? state.state_label ?? state.label ?? state.name ?? state.state;
      return { id: id != null ? String(id) : '', label: label != null ? String(label) : '' };
    })
    .filter((s) => s.id && s.label);
}

function mapCitiesResponse(response) {
  const apiCities = Array.isArray(response?.data)
    ? response.data
    : response?.data?.cities || response?.data?.master_cities || [];
  return apiCities
    .map((city) => {
      const id = city.city_id ?? city.id ?? city.value;
      const label = city.city_name ?? city.city_label ?? city.label ?? city.name ?? city.city;
      return { id: id != null ? String(id) : '', label: label != null ? String(label) : '' };
    })
    .filter((c) => c.id && c.label);
}

async function resolveLocationLabels(countryId, stateId, cityId) {
  const cid = countryId != null && String(countryId).trim() !== '' ? String(countryId).trim() : '';
  const sid = stateId != null && String(stateId).trim() !== '' ? String(stateId).trim() : '';
  const cyid = cityId != null && String(cityId).trim() !== '' ? String(cityId).trim() : '';
  if (!cid) {
    return { country: '—', state: '—', city: '—' };
  }
  try {
    const cRes = await CommonService.get('/master/company-Country');
    const countries = mapCountriesResponse(cRes);
    const countryLabel = countries.find((c) => c.id === cid)?.label || cid;

    let stateLabel = '—';
    if (sid) {
      const sRes = await CommonService.get(`/master/company-State/${cid}`);
      const states = mapStatesResponse(sRes);
      stateLabel = states.find((s) => s.id === sid)?.label || sid;
    }

    let cityLabel = '—';
    if (cyid && sid) {
      const cityRes = await CommonService.get(`/master/company-City/${sid}`);
      const cities = mapCitiesResponse(cityRes);
      cityLabel = cities.find((c) => c.id === cyid)?.label || cyid;
    }

    return { country: countryLabel, state: stateLabel, city: cityLabel };
  } catch {
    return {
      country: cid,
      state: sid || '—',
      city: cyid || '—',
    };
  }
}

const EL_SERVICE_LABELS = {
  cargoInsurance: 'Cargo Insurance',
  customsClearance: 'Customs Clearance',
  tempStorageVenue: 'Temporary Storage at Venue',
  boothSetupDismantling: 'Booth Setup & Dismantling',
  realTimeTracking: 'Real-time Shipment Tracking',
  hazardousShipment: 'Hazardous Shipment',
  labourSupport: 'Labour Support at Venue',
};

function displayValue(value) {
  if (value === null || value === undefined || (typeof value === 'string' && value.trim() === '')) {
    return <span className="elrv-empty">—</span>;
  }
  return String(value);
}

function formatDateSlash(value) {
  if (value == null || String(value).trim() === '') return '—';
  const s = String(value).trim().slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
    const [y, m, d] = s.split('-');
    return `${d}/${m}/${y}`;
  }
  return s;
}

function formatSubmittedAt(value) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  const datePart = date.toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
  const withComma = datePart.replace(/^(\d{1,2} \w+) (\d{4})$/, '$1, $2');
  const timePart = date.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
  return `${withComma} at ${timePart}`;
}

function formatStatusBadge(status) {
  const s = String(status || 'pending').toLowerCase().replace(/\s+/g, '_');
  const label = String(status || 'pending')
    .replace(/_/g, ' ')
    .toUpperCase();
  return { className: `elrv-status-badge elrv-status-badge--${s}`, label };
}

function logisticsModeLabel(modeId) {
  if (modeId == null || String(modeId).trim() === '') return '—';
  const id = String(modeId).toLowerCase();
  const m = LOGISTICS_TRANSPORT_MODES.find((x) => x.id === id);
  return m ? m.label : String(modeId);
}

function packagingLabel(v) {
  if (v == null || String(v).trim() === '') return '—';
  const s = String(v).trim();
  return EL_PACKAGING_OPTIONS.includes(s) ? s : s;
}

function formatDeclaredValue(payload) {
  const amount = payload?.declaredValueUsd;
  if (amount == null || String(amount).trim() === '') return '—';
  const currency =
    payload?.declaredValueCurrency != null && String(payload.declaredValueCurrency).trim() !== ''
      ? String(payload.declaredValueCurrency).trim().toUpperCase()
      : 'USD';
  return `${amount} ${currency}`;
}

function Field({ label, value, multiline, plain, scroll }) {
  if (plain) {
    return (
      <div className="elrq-field elrv-field elrv-field--plain">
        <div className="elrq-label">{label}</div>
        <div
          className={`elrv-plain-value${multiline ? ' elrv-plain-value--multiline' : ''}${
            scroll ? ' elrv-plain-value--scroll' : ''
          }`}
        >
          {displayValue(value)}
        </div>
      </div>
    );
  }
  return (
    <div className="elrq-field elrv-field">
      <div className="elrq-label">{label}</div>
      <div className={`elrv-readonly${multiline ? ' elrv-readonly--multiline' : ''}`}>
        {displayValue(value)}
      </div>
    </div>
  );
}

function Section({ title, tone, icon, children, className = '' }) {
  return (
    <section className={`elrq-block elrq-block--${tone} elrv-block ${className}`.trim()}>
      <header className="elrq-block-head">
        <span className="elrq-block-icon" aria-hidden>
          {icon}
        </span>
        <h2 className="elrq-block-title">{title}</h2>
      </header>
      <div className="elrq-block-body">{children}</div>
    </section>
  );
}

/**
 * @param {{ payload: Record<string, unknown>, embedded?: boolean, hideSummary?: boolean, allocator?: object|null }} props
 */
export default function ExhibitionLogisticsRfqSubmittedView({
  payload,
  embedded = false,
  showStaffDetails = false,
  hideSummary = false,
  allocator = null,
}) {
  const rfq = payload || {};
  const [orgLoc, setOrgLoc] = useState({ country: '…', state: '…', city: '…' });
  const [venueLoc, setVenueLoc] = useState({ country: '…', state: '…', city: '…' });
  const [locReady, setLocReady] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLocReady(false);
    (async () => {
      const [o, v] = await Promise.all([
        resolveLocationLabels(rfq.orgCountry, rfq.orgState, rfq.orgCity),
        resolveLocationLabels(rfq.venueCountry, rfq.venueState, rfq.venueCity),
      ]);
      if (cancelled) return;
      setOrgLoc(o);
      setVenueLoc(v);
      setLocReady(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [rfq.orgCountry, rfq.orgState, rfq.orgCity, rfq.venueCountry, rfq.venueState, rfq.venueCity]);

  const additionalServices =
    rfq.additionalServices && typeof rfq.additionalServices === 'object' && !Array.isArray(rfq.additionalServices)
      ? rfq.additionalServices
      : {};

  const selectedServices = useMemo(
    () =>
      Object.entries(additionalServices)
        .filter(([, on]) => Boolean(on))
        .map(([k]) => EL_SERVICE_LABELS[k] || k),
    [additionalServices],
  );

  const statusBadge = formatStatusBadge(rfq.status);
  const rfqRef = rfq.rfqId || rfq.rfqReference || rfq.rfqReferenceNumber || '—';
  const buyerLabel = rfq.buyerName || rfq.contactPerson || '—';
  const submittedAt = rfq.createdAt || rfq.submittedDate;
  const assignee = showStaffDetails ? getCurrentAssigneeDisplay(rfq) : { name: '', role: null };
  const allocatorContact = allocator && typeof allocator === 'object' ? allocator : null;
  const allocatorName =
    allocatorContact?.name != null && String(allocatorContact.name).trim() !== ''
      ? String(allocatorContact.name).trim()
      : null;
  const allocatorRoleLabel =
    allocatorContact?.roleLabel != null && String(allocatorContact.roleLabel).trim() !== ''
      ? String(allocatorContact.roleLabel).trim()
      : null;
  const allocatorContactPerson =
    allocatorName != null
      ? `${allocatorName}${allocatorRoleLabel ? ` (${allocatorRoleLabel})` : ''}`
      : null;
  const allocatorPhone =
    allocatorContact?.phone != null && String(allocatorContact.phone).trim() !== ''
      ? String(allocatorContact.phone).trim()
      : null;
  const allocatorEmail =
    allocatorContact?.email != null && String(allocatorContact.email).trim() !== ''
      ? String(allocatorContact.email).trim()
      : null;
  const showAllocatorContact = Boolean(allocatorContactPerson || allocatorPhone || allocatorEmail);

  const handleDownload = () => {
    try {
      downloadExhibitionLogisticsRfqPdf({
        rfq,
        orgLoc,
        venueLoc,
        selectedServices,
        formatSubmittedAt,
        formatDateSlash,
        logisticsModeLabel,
        formatDeclaredValue,
      });
    } catch (err) {
      console.error('Failed to generate exhibition RFQ PDF', err);
    }
  };

  return (
    <div className="elrv-root">
      {!embedded ? (
        <div className="elrv-hero-row">
          <div className="elrv-hero-row__text">
            <h1 className="elrv-title">Exhibition Logistics RFQ</h1>
            <p className="elrv-subtitle">End-to-end logistics solution for your exhibition participation.</p>
          </div>
          <aside className="elrv-status-panel" aria-label="RFQ status">
            <div className="elrv-status-panel__seg">
              <span className="elrv-status-panel__label">Status</span>
              <span className={statusBadge.className}>{statusBadge.label}</span>
            </div>
            <div className="elrv-status-panel__divider" aria-hidden />
            <div className="elrv-status-panel__seg elrv-status-panel__seg--date">
              <span className="elrv-status-panel__sublabel">Submitted on</span>
              <span className="elrv-submitted-on">
                <LuCalendar aria-hidden />
                <span>{formatSubmittedAt(submittedAt)}</span>
              </span>
            </div>
          </aside>
        </div>
      ) : null}

      {!hideSummary ? (
      <div className="elrv-summary">
        <div className="elrv-summary__cell">
          <span className="elrv-summary__icon" aria-hidden>
            <LuFileText />
          </span>
          <div className="elrv-summary__text">
            <span className="elrv-summary__label">RFQ ID</span>
            <strong className="elrv-summary__value">{rfqRef}</strong>
          </div>
        </div>
        <div className="elrv-summary__cell">
          <span className="elrv-summary__icon" aria-hidden>
            <LuUser />
          </span>
          <div className="elrv-summary__text">
            <span className="elrv-summary__label">Buyer</span>
            <strong className="elrv-summary__value">{buyerLabel}</strong>
          </div>
        </div>
        <div className="elrv-summary__cell">
          <span className="elrv-summary__icon" aria-hidden>
            <LuBuilding2 />
          </span>
          <div className="elrv-summary__text">
            <span className="elrv-summary__label">Company</span>
            <strong className="elrv-summary__value">{rfq.companyName || '—'}</strong>
          </div>
        </div>
        <div className="elrv-summary__cell">
          <span className="elrv-summary__icon" aria-hidden>
            <LuPhone />
          </span>
          <div className="elrv-summary__text">
            <span className="elrv-summary__label">Contact</span>
            <strong className="elrv-summary__value">{rfq.contactNumber || '—'}</strong>
          </div>
        </div>
        <div className="elrv-summary__cell">
          <span className="elrv-summary__icon" aria-hidden>
            <LuMail />
          </span>
          <div className="elrv-summary__text">
            <span className="elrv-summary__label">Email</span>
            <strong className="elrv-summary__value">{rfq.companyEmail || '—'}</strong>
          </div>
        </div>
        {showStaffDetails && assignee.name ? (
          <div className="elrv-summary__cell">
            <span className="elrv-summary__icon" aria-hidden>
              <LuUser />
            </span>
            <div className="elrv-summary__text">
              <span className="elrv-summary__label">Current Assignee</span>
              <strong className="elrv-summary__value">
                {assignee.name}
                {assignee.role ? ` (${assignee.role})` : ''}
              </strong>
            </div>
          </div>
        ) : null}
      </div>
      ) : null}

      <div className="elrv-card-inner">
        <div className="elrv-grid-row elrv-grid-row--2">
          <Section icon={<FaUser />} title="Contact Information" tone="lavender">
            {showAllocatorContact || hideSummary ? (
              <>
                <div className="elrq-row elrq-row--2 elrv-loc-row">
                  <Field plain label="Contact Person" value={allocatorContactPerson} />
                  <Field plain label="Contact Number" value={allocatorPhone} />
                </div>
                <div className="elrq-row elrq-row--2 elrv-loc-row">
                  <Field plain label="Role" value={allocatorRoleLabel} />
                  <Field plain label="Email" value={allocatorEmail} />
                </div>
              </>
            ) : (
              <>
                <div className="elrq-row elrq-row--2 elrv-loc-row">
                  <Field plain label="Contact Person" value={rfq.contactPerson} />
                  <Field plain label="Contact Number" value={rfq.contactNumber} />
                </div>
                <div className="elrq-row elrq-row--2 elrv-loc-row">
                  <Field plain label="Company Name" value={rfq.companyName} />
                  <Field plain label="Email" value={rfq.companyEmail} />
                </div>
              </>
            )}
          </Section>

          <Section icon={<FaFileAlt />} title="Exhibition Details" tone="blue">
            <div className="elrq-row elrq-row--2 elrv-loc-row">
              <Field plain label="Exhibition/Expo Name" value={rfq.exhibitionName} />
              <Field plain label="Setup Date" value={formatDateSlash(rfq.setupDate)} />
            </div>
            <div className="elrq-row elrq-row--2 elrv-loc-row">
              <Field plain label="Exhibition Start Date" value={formatDateSlash(rfq.exhibitionStartDate)} />
              <Field plain label="Exhibition End Date" value={formatDateSlash(rfq.exhibitionEndDate)} />
            </div>
          </Section>
        </div>

        <div className="elrv-grid-row elrv-grid-row--2">
          <Section icon={<FaListUl />} title="Organization Location" tone="green">
            {!locReady ? (
              <p className="elrv-muted">Loading location names…</p>
            ) : (
              <>
                <div className="elrq-row elrq-row--2 elrv-loc-row">
                  <Field plain label="Country" value={orgLoc.country} />
                  <Field plain label="State / Province" value={orgLoc.state} />
                </div>
                <div className="elrq-row elrq-row--2 elrv-loc-row">
                  <Field plain label="City" value={orgLoc.city} />
                  <Field plain label="Pincode / ZIP" value={rfq.orgPincode} />
                </div>
                <Field plain label="Complete Address" value={rfq.orgAddress} multiline />
              </>
            )}
          </Section>

          <Section icon={<FaMapMarkerAlt />} title="Exhibition Venue Location" tone="sky">
            {!locReady ? (
              <p className="elrv-muted">Loading location names…</p>
            ) : (
              <>
                <div className="elrq-row elrq-row--2 elrv-loc-row">
                  <Field plain label="Country" value={venueLoc.country} />
                  <Field plain label="State / Province" value={venueLoc.state} />
                </div>
                <div className="elrq-row elrq-row--2 elrv-loc-row">
                  <Field plain label="City" value={venueLoc.city} />
                  <Field plain label="Pincode / ZIP" value={rfq.venuePincode} />
                </div>
                <Field plain label="Complete Address" value={rfq.venueAddress} multiline />
                <div className="elrv-field-spaced-top">
                  <Field plain label="Booth Number" value={rfq.boothNumber} />
                </div>
              </>
            )}
          </Section>
        </div>

        <div className="elrv-grid-row elrv-grid-row--2">
          <Section icon={<FaBriefcase />} title="Cargo & Materials Details" tone="orange">
            <Field plain label="Materials Description" value={rfq.materialsDescription || rfq.goodsDescription} multiline />
            <div className="elrq-row elrq-row--5 elrv-cargo-metrics">
              <Field
                plain
                label="Total Weight"
                value={
                  rfq.totalWeightKg != null && String(rfq.totalWeightKg).trim() !== ''
                    ? `${rfq.totalWeightKg} ${rfq.totalWeightUnit || 'KG'}`
                    : null
                }
              />
              <Field plain label="Number of Packages" value={rfq.numberOfPackages} />
              <Field plain label="Dimensions (L × W × H)" value={formatExhibitionDimensionsDisplay(rfq) || null} />
              <Field plain label="Declared Value" value={formatDeclaredValue(rfq)} />
              <Field plain label="Packaging Type" value={packagingLabel(rfq.packagingType)} />
            </div>
          </Section>

          <Section
            icon={<FaTruck />}
            title="Logistics Preferences"
            tone="purple"
            className="elrv-block--logistics-prefs"
          >
            <div className="elrq-row elrq-row--2 elrv-loc-row">
              <Field plain label="Preferred Logistics Mode" value={logisticsModeLabel(rfq.logisticsMode)} />
              <Field plain label="Urgency Level" value={rfq.urgencyLevel} />
            </div>
            <Field
              plain
              scroll
              label="Special Handling Requirements"
              value={rfq.specialHandling}
              multiline
            />
          </Section>
        </div>

        <Section icon={<FaReply />} title="Return Logistics" tone="rose">
          <label className="elrv-return-check">
            <input type="checkbox" checked={Boolean(rfq.returnToOrigin ?? rfq.returnLogisticsRequired)} readOnly disabled />
            <span>Exhibition materials need to be brought back to country of origin.</span>
          </label>
        </Section>

        <Section icon={<FaCog />} title="Additional Services Required" tone="slate">
          {selectedServices.length === 0 ? (
            <p className="elrv-muted">None selected.</p>
          ) : (
            <div className="elrv-svc-grid">
              {selectedServices.map((label) => (
                <div key={label} className="elrv-svc-item">
                  <FaCheck className="elrv-svc-check" aria-hidden />
                  {label}
                </div>
              ))}
            </div>
          )}
        </Section>

        {(!embedded || hideSummary) ? (
          <footer className="elrv-footer">
            <p className="elrv-footer__meta">
              RFQ ID: {rfqRef}
              <br />
              Submitted on {formatSubmittedAt(submittedAt)}
            </p>
            <button type="button" className="elrv-download-btn" onClick={handleDownload}>
              <LuDownload aria-hidden />
              Download RFQ
            </button>
          </footer>
        ) : null}
      </div>
    </div>
  );
}
